import Link from "next/link";
export interface AlternatingFeature {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  headline: string;
  description: string;
  imageSourcePath: string;
  buttonText: string;
  buttonHref: string;
}

interface AlternatingFeatureSectionProps {
  features: AlternatingFeature[];
}

interface SingleSectionProps {
  feature: AlternatingFeature;
}

const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(" ");
};

const ImageRightColumn: React.FC<SingleSectionProps> = ({ feature }) => {
  return (
    <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
      <div className="px-4 max-w-xl mx-auto sm:px-6 lg:py-32 lg:max-w-none lg:mx-0 lg:px-0">
        <div>
          <div>
            <span className="h-12 w-12 rounded-md flex items-center justify-center bg-gradient-to-r from-sky-600 to-blue-600">
              <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
            </span>
          </div>
          <div className="mt-6">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {feature.headline}
            </h2>
            <p className="mt-4 text-lg text-gray-500">{feature.description}</p>
            <div className="mt-6">
              <Link href={feature.buttonHref}>
                <a className="inline-flex bg-gradient-to-r from-sky-600 to-blue-600 bg-origin-border px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white hover:from-sky-700 hover:to-blue-700">
                  {feature.buttonText}
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 sm:mt-16 lg:mt-0">
        <div className="px-4 md:pr-0   md:pl-6 md:-mr-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
          {/* TODO: See if div wrapper + relative known issue re: next/image is actually solvable,
          and monitor these load times to see if "unoptimized" images are behaving poorly */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:left-0 lg:h-full lg:w-auto lg:max-w-none"
            src={feature.imageSourcePath}
            alt="Inbox user interface"
          />
        </div>
      </div>
    </div>
  );
};

const ImageLeftColumn: React.FC<SingleSectionProps> = ({ feature }) => {
  return (
    <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
      <div className="px-4 max-w-xl mx-auto sm:px-6 lg:py-32 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-2">
        <div>
          <div>
            <span className="h-12 w-12 rounded-md flex items-center justify-center bg-gradient-to-r from-sky-600 to-blue-600">
              <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
            </span>
          </div>
          <div className="mt-6">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {feature.headline}
            </h2>
            <p className="mt-4 text-lg text-gray-500">{feature.description}</p>
            <div className="mt-6">
              <a
                href={feature.buttonHref}
                className="inline-flex bg-gradient-to-r from-sky-600 to-blue-600 bg-origin-border px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white hover:from-sky-700 hover:to-blue-700"
              >
                {feature.buttonText}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 sm:mt-16 lg:mt-0 lg:col-start-1">
        <div className="px-4 md:pl-0 md:pr-6 md:-ml-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
          {/* TODO: See above */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:right-0 lg:h-full lg:w-auto lg:max-w-none"
            src={feature.imageSourcePath}
            alt="Customer profile user interface"
          />
        </div>
      </div>
    </div>
  );
};

const AlternatingFeatureSection: React.FC<AlternatingFeatureSectionProps> = ({
  features,
}) => {
  return (
    <div className="relative pt-16 pb-32 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-gray-100"
      />
      <ul role="list">
        {features.map((feature: AlternatingFeature, idx: number) => (
          <li key={idx} className="w-full">
            <div className={classNames(idx === 0 ? "relative" : "mt-24")}>
              {idx % 2 === 0 ? (
                <ImageRightColumn feature={feature} />
              ) : (
                <ImageLeftColumn feature={feature} />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AlternatingFeatureSection;
